import { ensureCsrfToken } from "./csrf";
import {
  OfflineError,
  assertCircuitClosed,
  recordFetchSuccess,
  recordServerFailure,
} from "./circuitBreaker";
import { reportResilienceState } from "@/components/AppResilience/resilience-store";

const RENEW_URL = "/api/v2/auth/renew";
const LOGIN_PATH = "/login";
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const RETRY_ATTEMPTS = 4;
const RETRY_BASE_MS = 400;
const RETRY_CAP_MS = 8_000;
// Prevent re-entrant renew calls if /renew itself 401s.
let renewInFlight: Promise<boolean> | null = null;

async function attemptRenew(): Promise<boolean> {
  try {
    const token = await ensureCsrfToken();
    const headers = new Headers();
    if (token) headers.set("X-CSRF-Token", token);
    const res = await fetch(RENEW_URL, {
      method: "POST",
      credentials: "include",
      headers,
    });
    return res.ok;
  } catch {
    return false;
  }
}

function resolveRenew(): Promise<boolean> {
  if (!renewInFlight) {
    renewInFlight = attemptRenew().finally(() => {
      renewInFlight = null;
    });
  }
  return renewInFlight;
}

function redirectToLogin(): void {
  if (
    typeof window !== "undefined" &&
    !window.location.pathname.startsWith(LOGIN_PATH)
  ) {
    const from = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const params = new URLSearchParams({ from });
    window.location.href = `${LOGIN_PATH}?${params.toString()}`;
  }
}

function redirectSuspendedToLogin(): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith(LOGIN_PATH)) return;
  const params = new URLSearchParams({
    from: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    reason: "suspended",
  });
  window.location.href = `${LOGIN_PATH}?${params.toString()}`;
}

export function withResilientFetch(baseFetch: typeof fetch): typeof fetch {
  const wrapped: typeof fetch = async (input, init) => {
    assertCircuitClosed(input);
    const res = await fetchWithTransientRetry(baseFetch, input, init);
    if (res.status === 423) {
      redirectSuspendedToLogin();
      return res;
    }
    if (res.status !== 401) {
      return res;
    }

    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    // Auth bootstrap owns its anonymous 401s. Renewing those same endpoints
    // cannot recover the rejected session and used to race the login CSRF mint.
    if (url.includes("/auth/")) {
      return res;
    }

    const renewed = await resolveRenew();
    if (!renewed) {
      redirectToLogin();
      return res;
    }

    const retry = await fetchWithTransientRetry(baseFetch, input, init);
    if (retry.status === 401) {
      redirectToLogin();
    }
    return retry;
  };
  return wrapped;
}

export const withRenewRetry = withResilientFetch;

async function fetchWithTransientRetry(
  baseFetch: typeof fetch,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): Promise<Response> {
  for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt += 1) {
    assertCircuitClosed(input);
    const res = await tryFetchOnce(baseFetch, input, init);
    if (!RETRYABLE_STATUS.has(res.status)) {
      recordFetchSuccess(input);
      return res;
    }
    recordServerFailure(input);
    if (isBalanceSnapshotRead(input, init)) return res;
    // The admin news fetch runs an OpenAI web_search curation the backend caps
    // at ~90s — longer than a typical proxy idle timeout, so a 504 here is a
    // gateway giving up, NOT a transient blip. Retrying would re-fire the whole
    // (billed, minutes-long) curation up to 4×. Surface the 504 instead.
    if (isSlowCurationPost(input, init)) return res;
    if (attempt === RETRY_ATTEMPTS - 1) return res;
    await delay(retryDelayMs(attempt));
  }
  throw new Error("fetchWithTransientRetry exhausted unexpectedly");
}

function isBalanceSnapshotRead(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): boolean {
  if (resolveMethod(input, init) !== "GET") return false;
  const pathname = resolvePathname(input);
  return (
    pathname === "/api/v2/balance" || pathname === "/api/v2/api/v2/balance"
  );
}

function isSlowCurationPost(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): boolean {
  if (resolveMethod(input, init) !== "POST") return false;
  return resolvePathname(input).endsWith("/admin/news/fetch");
}

function resolveMethod(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): string {
  const requestMethod = input instanceof Request ? input.method : "GET";
  return (init?.method ?? requestMethod).toUpperCase();
}

function resolvePathname(input: RequestInfo | URL): string {
  const raw =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  const origin =
    typeof window === "undefined" ? "http://localhost" : window.location.origin;
  return new URL(raw, origin).pathname;
}

async function tryFetchOnce(
  baseFetch: typeof fetch,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): Promise<Response> {
  try {
    return await baseFetch(input, init);
  } catch (error) {
    if (
      error instanceof TypeError &&
      typeof navigator !== "undefined" &&
      navigator.onLine === false
    ) {
      reportResilienceState("offline", "QB-NET-OFFLINE");
      throw new OfflineError();
    }
    reportResilienceState("reconnecting", "QB-NET-FETCH");
    throw error;
  }
}

function retryDelayMs(attempt: number): number {
  const exponential = Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_CAP_MS);
  return Math.round(exponential + Math.random() * 100);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
